import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, ChevronLeft, ChevronRight, FileText, MessageSquare, Mic, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { markOnboardingComplete } from '@/utils/onboarding';
import { useCreateProject } from '@/api/projects/create-project';
import { storage } from '@/utils/storage';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage
});

type ProjectChoice = 'interview' | 'form' | null;

const totalSteps = 5;

const steps = [
  {
    title: 'Bienvenue sur BeSpark',
    subtitle: "L'intelligence produit collaborative",
    content: (
      <div className="space-y-6 w-full flex flex-col items-center">
        <img src="/bespark-ai.webp" alt="BeSpark" className="h-24 w-24" />
        <h2 className="text-4xl md:text-5xl font-bold text-center">
          Transformez les feedbacks en
          <span className="block bg-linear-to-r from-primary via-purple-600 to-secondary bg-clip-text text-transparent mt-2">
            leviers de développement
          </span>
        </h2>
        <p className="text-xl text-muted-foreground max-w-2xl text-center leading-relaxed">
          BeSpark est une solution d&apos;intelligence produit conçue pour centraliser, analyser et prioriser les idées
          et feedbacks liés à vos produits ou services.
        </p>
        <p className="text-lg text-muted-foreground max-w-2xl text-center">
          L&apos;IA transforme la gestion du feedback en un processus{' '}
          <span className="text-foreground font-semibold">continu, naturel et intelligent</span>.
        </p>
      </div>
    )
  },
  {
    title: 'Trois fonctionnalités clés',
    subtitle: "Un écosystème vivant d'intelligence produit",
    content: (
      <div className="space-y-8 w-full flex flex-col items-center">
        <div className="grid md:grid-cols-3 gap-6 w-full max-w-5xl">
          <FeatureCard
            icon={<MessageSquare className="h-10 w-10 text-white" />}
            title="Collecte de feedback"
            description={
              <>
                Créez votre <span className="font-semibold text-foreground">Journal des Idées</span> en capturant les
                retours utilisateurs. L&apos;IA reformule, enrichit, classe et priorise automatiquement chaque idée.
              </>
            }
            gradient="from-purple-500 to-blue-500"
          />
          <FeatureCard
            icon={<Users className="h-10 w-10 text-white" />}
            title="Création de campagne"
            description={
              <>
                Lancez des <span className="font-semibold text-foreground">campagnes de collecte</span> ciblées pour vos
                utilisateurs finaux via des formulaires simples ou des interviews vocales.
              </>
            }
            gradient="from-blue-500 to-cyan-500"
          />
          <FeatureCard
            icon={<Mic className="h-10 w-10 text-white" />}
            title="Interview vocale"
            description={
              <>
                Un assistant vocal interroge les utilisateurs pour recueillir des retours{' '}
                <span className="font-semibold text-foreground">riches et contextuels</span>, sans contrainte de
                formulaire.
              </>
            }
            gradient="from-cyan-500 to-teal-500"
          />
        </div>
      </div>
    )
  },
  {
    title: 'Créez votre projet',
    subtitle: 'Donnez un nom à votre projet pour commencer',
    content: null
  },
  {
    title: 'Comment voulez-vous commencer ?',
    subtitle: 'Décrivez votre projet via une interview IA ou un formulaire simple',
    content: null
  },
  {
    title: 'Décrivez votre projet',
    subtitle: 'Fournissez des détails pour aider l’IA à mieux comprendre votre produit',
    content: null
  }
];

function OnboardingPage() {
  const navigate = useNavigate();
  const organizationId = storage.getOrganizationId();
  const createProjectMutation = useCreateProject();
  const [currentStep, setCurrentStep] = useState(0);
  const [projectChoice, setProjectChoice] = useState<ProjectChoice>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');

  if (!organizationId) {
    throw new Error('Organization ID is required for onboarding.');
  }

  const handleComplete = async (skipProject = false) => {
    if (skipProject) {
      storage.setProjectId(null);
      markOnboardingComplete();
      await navigate({ to: '/campaigns' });
      return;
    }

    try {
      const name = projectName.trim() || 'Mon projet';
      const response = await createProjectMutation.mutateAsync({
        organizationId,
        name,
        description: projectDescription || undefined
      });
      storage.setProjectId(response.id);
      markOnboardingComplete();

      toast.success('Bienvenue sur BeSpark!', {
        description: 'Votre projet a été créé avec succès.',
        action: organizationId
          ? {
              label: 'Voir mon organisation',
              onClick: () => navigate({ to: '/organizations/$id', params: { id: organizationId } })
            }
          : undefined
      });

      navigate({ to: '/campaigns' });
    } catch (error) {
      console.error(error);
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 403) {
        toast.error('Session expirée', {
          description: 'Reconnectez-vous pour créer un projet.'
        });
        navigate({ to: '/login', search: { redirect: '/onboarding' } });
      } else {
        toast.error('Création du projet impossible', {
          description: 'Merci de réessayer ou de vérifier votre connexion.'
        });
      }
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep((prev) => {
          const nextStep = Math.min(prev + 1, totalSteps - 1);
          // Si on choisit le formulaire, on saute l'étape de choix
          if (prev === 3 && projectChoice === 'form') {
            return Math.min(nextStep + 1, totalSteps - 1);
          }
          return nextStep;
        });
        setIsTransitioning(false);
      }, 200);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep((prev) => {
          const prevStep = Math.max(prev - 1, 0);
          // Si on revient depuis le formulaire, on retourne à l'étape de choix
          if (prev === 4 && projectChoice === 'form') {
            return 3;
          }
          return prevStep;
        });
        setIsTransitioning(false);
      }, 200);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-background to-muted/20 flex flex-col">
      <div className="w-full px-4 pt-8 pb-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="group-hover:scale-110 transition-transform duration-300">
                <img src="/bespark-ai.webp" alt="BeSpark" className="h-12 w-12" />
              </div>
              <span className="text-xl font-bold bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">
                BeSpark
              </span>
            </Link>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="container mx-auto max-w-5xl w-full">
          <Card className="border-2 shadow-xl animate-fade-in">
            <CardContent className="p-8 md:p-12">
              <div className="space-y-6">
                <div className="text-center space-y-2 mb-8">
                  <h1 className="text-3xl md:text-4xl font-bold">{steps[currentStep].title}</h1>
                  {steps[currentStep].subtitle && (
                    <p className="text-lg text-muted-foreground">{steps[currentStep].subtitle}</p>
                  )}
                </div>

                <div
                  className={`min-h-[400px] flex items-center justify-center transition-opacity duration-300 ${
                    isTransitioning ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  {currentStep === 2 ? (
                    <ProjectNameStep projectName={projectName} setProjectName={setProjectName} />
                  ) : currentStep === 3 ? (
                    <LastStep projectChoice={projectChoice} setProjectChoice={setProjectChoice} onNext={handleNext} />
                  ) : currentStep === 4 ? (
                    <ProjectForm
                      projectName={projectName}
                      projectDescription={projectDescription}
                      setProjectName={setProjectName}
                      setProjectDescription={setProjectDescription}
                      isSubmitting={createProjectMutation.isPending}
                    />
                  ) : (
                    steps[currentStep].content
                  )}
                </div>

                <div className="flex items-center justify-between pt-6">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                    className="gap-2"
                  >
                    <ChevronLeft className="h-5 w-5" />
                    Précédent
                  </Button>

                  <div className="flex justify-center gap-2">
                    {Array.from({ length: totalSteps }).map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setIsTransitioning(true);
                          setTimeout(() => {
                            setCurrentStep(index);
                            setIsTransitioning(false);
                          }, 200);
                        }}
                        className={`h-2 cursor-pointer rounded-full transition-all duration-300 ${
                          index === currentStep
                            ? 'w-8 bg-primary'
                            : index < currentStep
                              ? 'w-2 bg-primary/50'
                              : 'w-2 bg-muted'
                        }`}
                        aria-label={`Aller à l'étape ${index + 1}`}
                      />
                    ))}
                  </div>

                  {currentStep === 3 ? (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleComplete(false)}
                      className="gap-2"
                      disabled={createProjectMutation.isPending || !projectName.trim()}
                    >
                      Pas maintenant
                    </Button>
                  ) : (
                    <Button
                      variant="hero"
                      size="lg"
                      onClick={currentStep === totalSteps - 1 ? () => handleComplete(false) : handleNext}
                      className="gap-2"
                      disabled={
                        createProjectMutation.isPending ||
                        (currentStep === 2 && !projectName.trim()) ||
                        (currentStep === totalSteps - 1 && !projectName.trim())
                      }
                    >
                      {currentStep === totalSteps - 1 ? 'Créer' : 'Suivant'}
                      {currentStep < totalSteps - 1 && <ChevronRight className="h-5 w-5" />}
                      {currentStep === totalSteps - 1 && <ArrowRight className="h-5 w-5" />}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  gradient
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  gradient: string;
}) {
  return (
    <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
      <CardContent className="p-6 space-y-4 text-center">
        <div
          className={`w-16 h-16 rounded-lg bg-linear-to-br ${gradient} p-4 mx-auto flex items-center justify-center`}
        >
          {icon}
        </div>
        <div className="space-y-1">
          <h3 className="text-xl font-semibold">{title}</h3>
          <p className="text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function LastStep({
  setProjectChoice,
  onNext
}: {
  projectChoice: ProjectChoice;
  setProjectChoice: (choice: ProjectChoice) => void;
  onNext: () => void;
}) {
  return (
    <div className="w-full max-w-3xl space-y-6">
      <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto">
        Pour compléter votre projet, choisissez la méthode qui vous convient le mieux pour décrire votre produit ou
        service.
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
          <CardContent className="p-6 space-y-4 text-center">
            <div className="w-16 h-16 rounded-lg bg-linear-to-br from-cyan-500 to-teal-500 p-4 mx-auto">
              <Mic className="h-full w-full text-white" />
            </div>
            <h3 className="text-xl font-semibold">Interview vocale rapide</h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Découvrez le fonctionnement de notre interview IA tout en renseignant les informations sur votre projet de
              manière naturelle et rapide.
            </p>
            <div className="pt-2">
              <Badge variant="outline" className="text-xs">
                ~2 minutes
              </Badge>
            </div>
            <div className="pt-4">
              <Button
                variant="hero"
                size="lg"
                onClick={() => {
                  setProjectChoice('interview');
                  onNext();
                }}
                className="w-full gap-2"
                disabled
              >
                <Mic className="h-4 w-4" />
                Démarrer l&apos;interview
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
          <CardContent className="p-6 space-y-4 text-center">
            <div className="w-16 h-16 rounded-lg bg-linear-to-br from-purple-500 to-blue-500 p-4 mx-auto">
              <FileText className="h-full w-full text-white" />
            </div>
            <h3 className="text-xl font-semibold">Formulaire simple</h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Renseignez rapidement les informations essentielles sur votre projet via un formulaire classique.
            </p>
            <div className="pt-2">
              <Badge variant="outline" className="text-xs">
                ~1 minute
              </Badge>
            </div>
            <div className="pt-4">
              <Button
                variant="hero"
                size="lg"
                onClick={() => {
                  setProjectChoice('form');
                  onNext();
                }}
                className="w-full gap-2"
              >
                <FileText className="h-4 w-4" />
                Remplir le formulaire
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ProjectForm({
  projectName,
  setProjectName,
  projectDescription,
  setProjectDescription,
  isSubmitting
}: {
  projectName: string;
  setProjectName: (name: string) => void;
  projectDescription: string;
  setProjectDescription: (desc: string) => void;
  isSubmitting: boolean;
}) {
  return (
    <form className="space-y-8 w-full max-w-3xl mx-auto">
      <div className="space-y-3">
        <Label htmlFor="projectName" className="text-base font-semibold">
          Nom du projet *
        </Label>
        <Input
          id="projectName"
          placeholder="Ex: Mon Application Web, Mon SaaS, Mon Produit..."
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="text-lg py-6"
          required
        />
      </div>
      <div className="space-y-3">
        <Label htmlFor="projectDescription" className="text-base font-semibold">
          Description du projet
        </Label>
        <textarea
          id="projectDescription"
          placeholder="Décrivez votre produit, sa mission, ses utilisateurs cibles, etc."
          value={projectDescription}
          onChange={(e) => setProjectDescription(e.target.value)}
          className="w-full min-h-[150px] text-lg p-4 border rounded-md"
        />
      </div>
      <Button type="submit" className="hidden" disabled={isSubmitting || !projectName.trim()}>
        Soumettre
      </Button>
    </form>
  );
}

function ProjectNameStep({
  projectName,
  setProjectName
}: {
  projectName: string;
  setProjectName: (name: string) => void;
}) {
  return (
    <div className="space-y-8 w-full max-w-3xl mx-auto">
      <div className="flex flex-col items-center space-y-6">
        <img src="/bespark-ai.webp" alt="BeSpark" className="h-24 w-24" />
        <p className="text-lg text-muted-foreground text-center max-w-2xl">
          Commençons par donner un nom à votre projet. Ce nom sera utilisé pour identifier votre espace de travail.
        </p>
      </div>

      <div className="space-y-3">
        <Label htmlFor="projectNameStep" className="text-base font-semibold">
          Nom du projet *
        </Label>
        <Input
          id="projectNameStep"
          placeholder="Ex: Mon Application Web, Mon SaaS, Mon Produit..."
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="text-lg py-6"
          autoFocus
        />
      </div>
    </div>
  );
}
